/**
 * feat-338 — CSV export of the current Questions table (client-side, from the loaded
 * rows, so it reflects the current filters, flagged-only and sort).
 *
 * Aggregates only. The API never sends a learner identifier, and this file adds none:
 * no ids of any kind are exported, only question wording, lesson names and the numbers
 * on screen. A suppressed row exports its text with every metric "hidden".
 */
import type { QuestionAnalysisFilters, QuestionRow, ResultMix } from '../../../../services/questionAnalysisService';
import { DEFAULT_SUPPRESS_BELOW_LEARNERS, FLAG, TYPE_LABEL } from './questionAnalysisFormat';

export const csvHidden = (floor: number) => `hidden (fewer than ${floor} learners)`;
export const CSV_NA = 'n/a';

export const CSV_COLUMNS = [
  'Question',
  'Lesson',
  'Unit',
  'Type',
  'Edited (UTC)',
  'Flags',
  'Enough data to flag',
  'Learners',
  'Answers',
  'Wrong 1st try %',
  'Right first try',
  'Typed wrong, multiple-choice fallback right',
  'Wrong, then right on retry',
  'Still wrong after retry',
  'Still wrong after retry %',
  'Typical answer time (s)',
  '1 in 10 took over (s)',
  'Answers left out of times (time cap)',
  'Quit here %',
  'AI-checked learners',
  'AI-rejected learners',
  // feat-354 — appended LAST so every existing column keeps its index. An integer,
  // 0 when there are none (a spreadsheet column must not be blank-for-zero).
  'Copies',
] as const;

const numCell = (n: number | null) => (n == null ? '' : n);
/** A withheld learner count (r3) is written as words, never 0 or blank. */
const countCell = (n: number | null, floor: number) => (n == null ? `fewer than ${floor}` : n);
/** feat-354: absent (older backend) ⇒ 0. */
const copiesCell = (r: QuestionRow) => r.copies ?? 0;
const pctCell = (f: number | null) => (f == null ? '' : String(Math.round(f * 1000) / 10));
const secCell = (ms: number | null) => (ms == null ? '' : String(Math.round(ms / 100) / 10));

/** Quote when needed, and neutralise spreadsheet formula prefixes. */
export function csvEscape(v: string | number): string {
  let s = String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function questionToCsvRow(r: QuestionRow, floor: number = DEFAULT_SUPPRESS_BELOW_LEARNERS): (string | number)[] {
  const ident = [
    r.question_text,
    r.drill_name,
    r.lesson_name,
    TYPE_LABEL[r.question_type],
    r.edited_at ?? '',
    r.flags.map((f) => FLAG[f].label).join('; '),
  ];
  if (r.suppressed) {
    // The copy count is a curriculum fact, not learner data: shown even when suppressed.
    return [...ident, 'no', ...Array(CSV_COLUMNS.length - ident.length - 2).fill(csvHidden(floor)), copiesCell(r)];
  }
  const mix: ResultMix = r.result_mix;
  return [
    ...ident,
    r.enough_data ? 'yes' : 'no',
    r.learners,
    r.attempts,
    pctCell(r.first_try_wrong_rate),
    mix.pass_first,
    mix.pass_mc_partial,
    mix.pass_retry,
    mix.fail,
    pctCell(r.still_wrong_after_retry_rate),
    secCell(r.median_answer_ms),
    secCell(r.p90_answer_ms),
    numCell(r.capped_count),
    pctCell(r.exit_rate),
    r.fitb ? countCell(r.fitb.ai_graded_learners, floor) : CSV_NA,
    r.fitb ? countCell(r.fitb.rejected_learners, floor) : CSV_NA,
    copiesCell(r),
  ];
}

export function buildQuestionsCsv(rows: QuestionRow[], floor: number = DEFAULT_SUPPRESS_BELOW_LEARNERS): string {
  const lines = [CSV_COLUMNS.map(csvEscape).join(',')];
  rows.forEach((r) => lines.push(questionToCsvRow(r, floor).map(csvEscape).join(',')));
  return `${lines.join('\r\n')}\r\n`;
}

export function csvFilename(tenantName: string, f: QuestionAnalysisFilters): string {
  const slug = (tenantName || 'tenant').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'tenant';
  return `question-analysis-${slug}-${f.start || 'all'}-to-${f.end || 'today'}.csv`;
}

/** Hand the text to the browser as a download (BOM so Excel reads UTF-8). */
export function downloadCsv(filename: string, text: string): void {
  const blob = new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
